import { clearNavigationGuardsAfterSessionChange, confirmNavigation } from './shared/navigationGuard';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdminPanelSettingsOutlined,
  ArrowBackOutlined,
  HomeOutlined,
  LogoutOutlined,
  PersonOutlined,
  SwitchAccountOutlined,
  TaskAltOutlined,
} from '@mui/icons-material';
import { Button } from '@mui/material';
import {
  AppShell,
  BrandThemeProvider,
  FeedbackSnackbar,
  FullPageState,
  PageHeaderIconProvider,
  ResultState,
  defaultBrandTheme,
} from './design-system';
import { ApiClient, ApiError, getAuthSession } from './api/client';
import type {
  AuthSession,
  InitialAdminSetupSession,
} from './api/types';
import { useAsyncResource } from './shared/useAsyncResource';
import { isHeydayFeatureAvailable, isHeydayPageReleased } from './config/heydayPageManifest';
import { heydayPageIcons } from './config/heydayPageIcons';
import { createFeatureReleaseHttpAdapter } from './app/adapters/featureReleaseHttpAdapter';
import type { FeatureReleaseUser } from './features/feature-release/public';
import { createHeydayNavigation, heydayPageLabel, isEmployeeHeydayPage, type HeydayPage } from './config/heydayNavigation';
import {
  canonicalizeCurrentHeydayLocation,
  currentHeydayReturnTo,
  HeydayRouteContent,
  navigateToHeydayPage,
  subscribeToHeydayLocation,
} from './app/routing';
import { createAdministratorHttpAdapter } from './app/adapters/administratorHttpAdapter';
import { createAbsenceHttpAdapter } from './app/adapters/absenceHttpAdapter';
import { createAbsenceSettingsHttpAdapter } from './app/adapters/absenceSettingsHttpAdapter';
import { createBulkOperationsHttpAdapter } from './app/adapters/bulkOperationsHttpAdapter';
import { createBulkReferenceAdapter } from './app/adapters/bulkReferenceAdapter';
import {
  createCertificatesHttpAdapter,
  createCertificateVerificationHttpAdapter,
} from './app/adapters/certificatesHttpAdapter';
import { createEmployeeHomeHttpAdapter } from './app/adapters/employeeHomeHttpAdapter';
import { createEmployeeDirectoryHttpAdapter } from './app/adapters/employeeDirectoryHttpAdapter';
import { createDocumentsHttpAdapter } from './app/adapters/documentsHttpAdapter';
import { createInboxHttpAdapter } from './app/adapters/inboxHttpAdapter';
import { createLifecycleHttpAdapter } from './app/adapters/lifecycleHttpAdapter';
import { createOperationsHomeHttpAdapter } from './app/adapters/operationsHomeHttpAdapter';
import { createOrganizationHttpAdapter } from './app/adapters/organizationHttpAdapter';
import { createPeopleOperationsHttpAdapter } from './app/adapters/peopleOperationsHttpAdapter';
import { createPeopleProfileHttpAdapter } from './app/adapters/peopleProfileHttpAdapter';
import { createRecruitingHttpAdapter } from './app/adapters/recruitingHttpAdapter';
import { createRewardsHttpAdapter } from './app/adapters/rewardsApi';
import { createEmploymentContractHttpAdapter } from './app/adapters/employmentContractHttpAdapter';
import { createStaffingOperationsHttpAdapter } from './app/adapters/staffingOperationsHttpAdapter';
import { createWorkflowApprovalSettingsHttpAdapter } from './app/adapters/workflowApprovalSettingsHttpAdapter';
import { createWorkflowHttpAdapter } from './app/adapters/workflowHttpAdapter';
import { createWorkflowOperationsHttpAdapter } from './app/adapters/workflowOperationsHttpAdapter';
import { createWorkflowSelfDelegationHttpAdapter } from './app/adapters/workflowSelfDelegationHttpAdapter';
import { createHeadcountHttpAdapter } from './app/adapters/headcountHttpAdapter';
import { createImpersonationAdapter } from './app/adapters/impersonationAdapter';
import { createIntegrationSettingsHttpAdapter } from './app/adapters/integrationSettingsHttpAdapter';
import { createTenantSettingsHttpAdapter } from './app/adapters/tenantSettingsHttpAdapter';
import { initialAdminSetupHttpAdapter } from './app/adapters/initialAdminSetupHttpAdapter';
import { LoginPage, parseLoginFailureReason } from './features/auth/public';
import { CertificateVerificationPage } from './features/certificates/public';
import {
  fixtureImpersonationTargets,
  ImpersonationDialog,
} from './features/security/public';
import type { OrganizationSettings } from './features/tenant-settings/public';
import { InitialAdminSetupPage } from './features/tenant-setup/public';
import { fixtureSessionFromStorage } from './fixtures/session';
import { HeaderSearch } from './app/search/HeaderSearch';
import { pageFromPathname } from './config/heydayRoutes';

type Page = HeydayPage;
const fixtureOrganizationBranding: OrganizationSettings = { displayName: '한결', version: 1, brandTheme: defaultBrandTheme };
const fixtureWorkflowTaskCount = 0;
const fixtureWorkflowRequestSummary = { inProgress: 1, completed: 0 };
const certificateVerificationPort = createCertificateVerificationHttpAdapter();

function googleLoginPath() {
  return `/api/auth/login?${new URLSearchParams({ returnTo: currentHeydayReturnTo() })}`;
}

function reloadAt(page: Page) {
  clearNavigationGuardsAfterSessionChange();
  window.dispatchEvent(new Event('heyday:session-changed'));
  navigateToHeydayPage(page, undefined, { replace: true });
  window.location.reload();
}

function sessionExpiryLabel(value: string) {
  const expiresAt = new Date(value);
  if (Number.isNaN(expiresAt.getTime())) return '만료 시각 확인 필요';
  return `${new Intl.DateTimeFormat('ko-KR', { hour: '2-digit', minute: '2-digit' }).format(expiresAt)}까지`;
}

function workflowComposerIsStandalone() {
  const page = pageFromPathname(window.location.pathname);
  const query = new URLSearchParams(window.location.search);
  return (page === 'my-workflows' && query.get('composer') === 'fullscreen')
    || (['my-workflow-requests', 'workflow-requests', 'my-tasks', 'inbox'].includes(page ?? '') && query.get('presentation') === 'fullscreen');
}

export function App() {
  const verificationToken = window.location.pathname.startsWith('/verify/certificates/') ? window.location.pathname.slice('/verify/certificates/'.length) : '';
  if (verificationToken) return <CertificateVerificationPage token={verificationToken} port={certificateVerificationPort} />;
  if (window.location.pathname === '/login') return <LoginPage reason={parseLoginFailureReason(window.location.search)} />;
  const fixtureMode = import.meta.env.VITE_USE_FIXTURES === 'true';
  const [session, setSession] = useState<AuthSession | undefined>(() => fixtureMode ? fixtureSessionFromStorage() : undefined);
  const [initialSetup, setInitialSetup] = useState<InitialAdminSetupSession>();
  const [authState, setAuthState] = useState<'loading' | 'setup' | 'authenticated' | 'unauthenticated' | 'error'>(() => fixtureMode ? 'authenticated' : 'loading');
  const [authError, setAuthError] = useState<string>();
  const [impersonationDialogOpen, setImpersonationDialogOpen] = useState(false);
  const [featureViewAsTarget, setFeatureViewAsTarget] = useState<FeatureReleaseUser>();
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
  const stoppingImpersonationRef = useRef(false);
  const [sessionActionError, setSessionActionError] = useState<string>();
  const authGeneration = useRef(0);
  const loginRedirectStarted = useRef(false);
  const requireLogin = useCallback(() => {
    if (loginRedirectStarted.current) return;
    clearNavigationGuardsAfterSessionChange();
    window.dispatchEvent(new Event('heyday:session-changed'));
    loginRedirectStarted.current = true;
    authGeneration.current += 1;
    const loginPath = googleLoginPath();
    setSession(undefined);
    setInitialSetup(undefined);
    setAuthState('unauthenticated');
    if (!fixtureMode) window.location.replace(loginPath);
  }, [fixtureMode]);
  const bootstrap = useCallback(() => {
    const generation = ++authGeneration.current;
    loginRedirectStarted.current = false;
    if (fixtureMode) {
      setSession(fixtureSessionFromStorage());
      setAuthState('authenticated');
      return;
    }
    setAuthState('loading');
    setAuthError(undefined);
    void getAuthSession().then(
      (value) => {
        if (generation !== authGeneration.current || loginRedirectStarted.current) return;
        if ('setupRequired' in value && value.setupRequired) {
          setSession(undefined);
          setInitialSetup(value);
          setAuthState('setup');
        } else {
          setInitialSetup(undefined);
          setSession(value);
          setAuthState('authenticated');
        }
      },
      (reason: unknown) => {
        if (generation !== authGeneration.current || loginRedirectStarted.current) return;
        setSession(undefined);
        if (reason instanceof ApiError && reason.status === 401) {
          requireLogin();
          return;
        }
        setAuthError(reason instanceof Error ? reason.message : '로그인 상태를 확인하지 못했습니다.');
        setAuthState('error');
      },
    );
  }, [fixtureMode, requireLogin]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const refreshSessionInFlight = useRef<Promise<void> | undefined>(undefined);
  const refreshSession = useCallback((): Promise<void> => {
    if (fixtureMode || loginRedirectStarted.current) return Promise.resolve();
    if (refreshSessionInFlight.current) return refreshSessionInFlight.current;
    const generation = authGeneration.current;
    const request = getAuthSession().then((value) => {
      if (generation !== authGeneration.current || loginRedirectStarted.current) return;
      if ('setupRequired' in value && value.setupRequired) {
        setSession(undefined);
        setInitialSetup(value);
        setAuthState('setup');
        return;
      }
      setSession((current) => JSON.stringify(current) === JSON.stringify(value) ? current : value);
    }).catch((reason: unknown) => {
      if (generation !== authGeneration.current || loginRedirectStarted.current) return;
      // A failed policy/session refresh must not keep displaying a stale release list.
      setSession(undefined);
      if (reason instanceof ApiError && reason.status === 401) requireLogin();
      else {
        authGeneration.current += 1;
        setAuthError(reason instanceof Error ? reason.message : '사용 가능한 기능을 확인하지 못했습니다.');
        setAuthState('error');
      }
    }).finally(() => {
      if (refreshSessionInFlight.current === request) refreshSessionInFlight.current = undefined;
    });
    refreshSessionInFlight.current = request;
    return request;
  }, [fixtureMode, requireLogin]);

  useEffect(() => {
    if (authState !== 'authenticated' || fixtureMode) return;
    const onFocus = () => { void refreshSession(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') void refreshSession(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [authState, fixtureMode, refreshSession]);

  const [page, setPage] = useState<Page>(canonicalizeCurrentHeydayLocation);
  const lastLocationPage = useRef(page);
  const [contentFocusKey, setContentFocusKey] = useState(0);
  const [immersiveWorkspace, setImmersiveWorkspace] = useState(workflowComposerIsStandalone);
  const [lastEmployeePage, setLastEmployeePage] = useState<Page>('my-home');
  const [lastAdminPage, setLastAdminPage] = useState<Page>('home');
  const navigate = useCallback((next: Page, query?: string, options?: { replace?: boolean }) => {
    const changesPage = next !== lastLocationPage.current;
    const navigated = navigateToHeydayPage(next, query, options);
    if (navigated && changesPage) setContentFocusKey((value) => value + 1);
    return navigated;
  }, []);

  useEffect(() => {
    const onLocationChange = () => {
      const nextPage = canonicalizeCurrentHeydayLocation();
      if (lastLocationPage.current !== nextPage) {
        lastLocationPage.current = nextPage;
        void refreshSession();
      }
      setPage(nextPage);
      setImmersiveWorkspace(workflowComposerIsStandalone());
    };
    return subscribeToHeydayLocation(onLocationChange);
  }, [refreshSession]);

  const requestWorkspace = isEmployeeHeydayPage(page) ? 'employee' : 'admin';
  const client = useMemo(() => {
    if (!session) return undefined;
    const generation = authGeneration.current;
    return new ApiClient({
      tenant: session.principal.tenant,
      csrfToken: session.csrfToken,
      workspace: requestWorkspace,
      onUnauthorized: () => {
        if (generation === authGeneration.current) requireLogin();
      },
      onImpersonationEnded: () => {
        if (generation === authGeneration.current && !loginRedirectStarted.current) reloadAt('people');
      },
    });
  }, [requireLogin, session, requestWorkspace]);
  const featureReleasePort = useMemo(
    () => client ? createFeatureReleaseHttpAdapter(client) : undefined,
    [client],
  );
  const bulkOperationsPort = useMemo(
    () => client ? createBulkOperationsHttpAdapter(client) : undefined,
    [client],
  );
  const bulkReferencePort = useMemo(() => client ? createBulkReferenceAdapter(client, (scope) => fixtureMode || Boolean(session?.principal.scopes.includes(scope))) : undefined, [client, fixtureMode, session?.principal.scopes]);
  const absencePort = useMemo(
    () => client ? createAbsenceHttpAdapter(client) : undefined,
    [client],
  );
  const absenceSettingsPort = useMemo(
    () => client ? createAbsenceSettingsHttpAdapter(client) : undefined,
    [client],
  );
  const certificatesPort = useMemo(
    () => client ? createCertificatesHttpAdapter(client) : undefined,
    [client],
  );
  const approvalSettingsPort = useMemo(
    () => client ? createWorkflowApprovalSettingsHttpAdapter(client) : undefined,
    [client],
  );
  const workflowPort = useMemo(
    () => client ? createWorkflowHttpAdapter(client) : undefined,
    [client],
  );
  const workflowSelfDelegationPort = useMemo(
    () => client ? createWorkflowSelfDelegationHttpAdapter(client) : undefined,
    [client],
  );
  const workflowOperationsPort = useMemo(
    () => client ? createWorkflowOperationsHttpAdapter(client) : undefined,
    [client],
  );
  const headcountPort = useMemo(
    () => client ? createHeadcountHttpAdapter(client) : undefined,
    [client],
  );
  const integrationSettingsPort = useMemo(
    () => client ? createIntegrationSettingsHttpAdapter(client) : undefined,
    [client],
  );
  const impersonationPort = useMemo(
    () => client ? createImpersonationAdapter(client, fixtureMode) : undefined,
    [client, fixtureMode],
  );
  const tenantSettingsPort = useMemo(
    () => client ? createTenantSettingsHttpAdapter(client) : undefined,
    [client],
  );
  const employeeDirectoryPort = useMemo(
    () => client ? createEmployeeDirectoryHttpAdapter(client) : undefined,
    [client],
  );
  const documentsPort = useMemo(
    () => client ? createDocumentsHttpAdapter(client) : undefined,
    [client],
  );
  const employeeHomePort = useMemo(
    () => client ? createEmployeeHomeHttpAdapter(client) : undefined,
    [client],
  );
  const inboxPort = useMemo(
    () => client ? createInboxHttpAdapter(client) : undefined,
    [client],
  );
  const operationsHomePort = useMemo(
    () => client ? createOperationsHomeHttpAdapter(client) : undefined,
    [client],
  );
  const administratorPort = useMemo(
    () => client ? createAdministratorHttpAdapter(client) : undefined,
    [client],
  );
  const rewardsApi = useMemo(
    () => client ? createRewardsHttpAdapter(client) : undefined,
    [client],
  );
  const contractApi = useMemo(() => client ? createEmploymentContractHttpAdapter(client) : undefined, [client]);
  const staffingOperationsPort = useMemo(
    () => client ? createStaffingOperationsHttpAdapter(client) : undefined,
    [client],
  );
  const organizationPort = useMemo(
    () => client ? createOrganizationHttpAdapter(client) : undefined,
    [client],
  );
  const peopleOperationsPort = useMemo(
    () => client ? createPeopleOperationsHttpAdapter(client) : undefined,
    [client],
  );
  const lifecyclePort = useMemo(
    () => client ? createLifecycleHttpAdapter(client) : undefined,
    [client],
  );
  const peopleProfilePort = useMemo(
    () => client ? createPeopleProfileHttpAdapter(client) : undefined,
    [client],
  );
  const recruitingPort = useMemo(
    () => client ? createRecruitingHttpAdapter(client) : undefined,
    [client],
  );

  const canReadWorkflowTasks = fixtureMode || Boolean(session?.principal.scopes.includes('workflow.request.approve'));
  const canReadSelfWorkflowRequests = fixtureMode || Boolean(session?.principal.scopes.includes('workflow.request.read.self'));
  const workflowTaskCount = useAsyncResource<number>(
    () => fixtureMode
      ? Promise.resolve(fixtureWorkflowTaskCount)
      : client && canReadWorkflowTasks
        ? client.listWorkflowTasks().then((response) => response.total)
        : Promise.resolve(0),
    [client, fixtureMode, canReadWorkflowTasks],
  );
  const workflowRequestSummary = useAsyncResource<{ inProgress: number; completed: number }>(
    () => fixtureMode
      ? Promise.resolve(fixtureWorkflowRequestSummary)
      : client && canReadSelfWorkflowRequests
        ? Promise.all([
          client.listWorkflowRequests({ status: 'IN_PROGRESS' }),
          client.listWorkflowRequests({ status: 'COMPLETED' }),
        ]).then(([inProgress, completed]) => ({ inProgress: inProgress.total, completed: completed.total }))
        : Promise.resolve({ inProgress: 0, completed: 0 }),
    [client, fixtureMode, canReadSelfWorkflowRequests],
  );

  const selfWorkerReference = isHeydayFeatureAvailable('people', session?.principal.availableFeatures) ? session?.principal.workerId : 'me';
  const accountWorker = useAsyncResource(
    () => !fixtureMode && client && session?.principal.workerId ? client.getWorker(selfWorkerReference ?? 'me') : Promise.resolve(undefined),
    [client, fixtureMode, session?.principal.workerId, selfWorkerReference],
  );
  const organizationBrandingResource = useAsyncResource<OrganizationSettings | undefined>(
    () => fixtureMode ? Promise.resolve(fixtureOrganizationBranding) : tenantSettingsPort ? tenantSettingsPort.getOrganizationSettings() : Promise.resolve(undefined),
    [fixtureMode, tenantSettingsPort],
  );
  const [organizationBrandingOverride, setOrganizationBrandingOverride] = useState<OrganizationSettings>();

  useEffect(() => { setOrganizationBrandingOverride(undefined); }, [session?.principal.tenant]);

  const stopImpersonation = useCallback(async () => {
    if (stoppingImpersonationRef.current || !impersonationPort || !confirmNavigation()) return;
    stoppingImpersonationRef.current = true;
    setStoppingImpersonation(true);
    setSessionActionError(undefined);
    try {
      await impersonationPort.stop();
      reloadAt('people');
    } catch (reason) {
      stoppingImpersonationRef.current = false;
      setSessionActionError(reason instanceof Error ? reason.message : '관리자 계정으로 돌아가지 못했습니다. 현재 사용자 보기 상태를 유지합니다.');
      setStoppingImpersonation(false);
    }
  }, [impersonationPort]);

  const navigation = useMemo(() => createHeydayNavigation({
    scopes: session?.principal.scopes ?? [],
    roles: session?.principal.roles ?? [],
    fixtureMode: fixtureMode && !session?.impersonation,
    availableFeatures: session?.principal.availableFeatures,
    managementApps: session?.principal.managementApps,
  }), [fixtureMode, session?.impersonation, session?.principal.roles, session?.principal.scopes, session?.principal.availableFeatures, session?.principal.managementApps]);

  const pageReleased = isHeydayPageReleased(page, session?.principal.availableFeatures, session?.principal.managementApps);
  useEffect(() => {
    if (authState !== 'authenticated' || navigation.visiblePages.has(page) || !pageReleased) return;
    navigate('my-home', undefined, { replace: true });
  }, [authState, navigate, navigation.visiblePages, page, pageReleased]);

  const currentPage = navigation.visiblePages.has(page) || !pageReleased ? page : 'my-home';
  const navigationMode = isEmployeeHeydayPage(currentPage) ? 'employee' : 'admin';

  useEffect(() => {
    if (authState !== 'authenticated') return;
    if (isEmployeeHeydayPage(currentPage)) setLastEmployeePage(currentPage);
    else setLastAdminPage(currentPage);
  }, [authState, currentPage]);

  if (authState === 'loading') return <FullPageState loading loadingOnly loadingLabel="로그인 상태 확인 중" />;
  if (authState === 'setup' && initialSetup) return <InitialAdminSetupPage setup={initialSetup} port={initialAdminSetupHttpAdapter} onComplete={(value) => {
    setInitialSetup(undefined);
    if (value.principal.scopes.includes('tenant.settings.read')) navigate('admin-getting-started', undefined, { replace: true });
    bootstrap();
  }} />;
  if (authState === 'unauthenticated') return <FullPageState loading loadingOnly loadingLabel="Google 로그인으로 이동 중" />;
  if (authState === 'error') return <FullPageState title="로그인 상태를 확인하지 못했습니다." description={authError} action={<Button variant="contained" onClick={bootstrap}>다시 시도</Button>} />;
  if (!session || !client || !featureReleasePort || !absencePort || !absenceSettingsPort || !bulkOperationsPort || !certificatesPort || !approvalSettingsPort || !workflowPort || !workflowOperationsPort || !workflowSelfDelegationPort || !headcountPort || !impersonationPort || !integrationSettingsPort || !tenantSettingsPort || !employeeDirectoryPort || !documentsPort || !employeeHomePort || !inboxPort || !operationsHomePort || !administratorPort || !rewardsApi || !staffingOperationsPort || !organizationPort || !peopleOperationsPort || !lifecyclePort || !peopleProfilePort || !recruitingPort) return null;

  const fixtureTarget = fixtureMode && session.impersonation ? fixtureImpersonationTargets.find((target) => target.workerId === session.principal.workerId) : undefined;
  const userLabel = fixtureMode ? fixtureTarget?.displayName || '이정연' : accountWorker.data?.descriptor || '로그인 사용자';
  const userOrganization = fixtureMode ? fixtureTarget?.organization : accountWorker.data?.primaryJob?.supervisoryOrganization?.descriptor;
  const canStartImpersonation = !session.impersonation &&
    session.principal.roles.includes('HEYDAY_ADMIN') &&
    session.principal.scopes.includes('security.impersonate');
  const activeSessionLabel = accountWorker.data?.descriptor || fixtureTarget?.displayName;
  const activeOrganizationBranding = organizationBrandingOverride ?? organizationBrandingResource.data;
  const organizationBrandingPending = !organizationBrandingOverride &&
    organizationBrandingResource.data === undefined &&
    organizationBrandingResource.error === undefined;
  return (<BrandThemeProvider setting={activeOrganizationBranding?.brandTheme}>
    <AppShell
      contentFocusKey={contentFocusKey}
      brandLabel="heyday"
      brandLogoUrl={activeOrganizationBranding?.logoUrl}
      brandLogoLabel={activeOrganizationBranding?.displayName}
      brandLogoLoading={organizationBrandingPending}
      groups={navigationMode === 'employee' ? navigation.employeeGroups : navigation.adminGroups}
      currentPage={currentPage}
      pageLabel={heydayPageLabel(currentPage)}
      pageVariant={currentPage === 'home' || currentPage === 'my-home' ? 'overview' : 'default'}
      navigationMode={navigationMode}
      navigationLabel={navigationMode === 'employee' ? '구성원 탐색' : '관리자 탐색'}
      sessionContext={session.impersonation ? {
        label: activeSessionLabel ? `${activeSessionLabel}님으로 보는 중` : '다른 사용자로 보는 중',
        description: `조회 전용 · ${sessionExpiryLabel(session.impersonation.expiresAt)}`,
        actionLabel: '관리자로 돌아가기',
        actionDisabled: stoppingImpersonation,
        onAction: () => void stopImpersonation(),
      } : undefined}
      navigationModeAction={navigationMode === 'employee'
        ? navigation.canAccessAdmin ? {
          label: '관리자 화면',
          icon: <AdminPanelSettingsOutlined />,
          onSelect: () => navigate(navigation.visiblePages.has(lastAdminPage) ? lastAdminPage : navigation.adminDefault),
        } : undefined
        : {
          label: '구성원 화면으로 돌아가기',
          icon: <ArrowBackOutlined />,
          onSelect: () => navigate(lastEmployeePage),
        }}
      onBrandClick={() => navigate('my-home')}
      account={{
        name: userLabel,
        organization: userOrganization,
        avatarUrl: accountWorker.data?.person?.profilePhotoUrl,
        actions: [{
          id: 'my-profile', label: '내 정보', icon: <PersonOutlined fontSize="small" />,
          onSelect: () => navigate('my-profile'),
        }, {
          id: 'my-home', label: '내 홈', icon: <HomeOutlined fontSize="small" />,
          onSelect: () => navigate('my-home'),
        }, {
          id: 'my-tasks', label: '내 할 일', icon: <TaskAltOutlined fontSize="small" />,
          onSelect: () => navigate('my-tasks'),
        }, ...(navigation.canAccessAdmin ? [{
          id: 'navigation-mode',
          label: navigationMode === 'employee' ? '관리자 화면' : '구성원 화면',
          icon: navigationMode === 'employee' ? <AdminPanelSettingsOutlined fontSize="small" /> : <ArrowBackOutlined fontSize="small" />,
          dividerBefore: true,
          onSelect: () => navigate(navigationMode === 'employee'
            ? navigation.visiblePages.has(lastAdminPage) ? lastAdminPage : navigation.adminDefault
            : lastEmployeePage),
        }] : []), ...(session.impersonation ? [{
          id: 'stop-impersonation', label: '관리자로 돌아가기',
          icon: <ArrowBackOutlined fontSize="small" />,
          dividerBefore: true,
          onSelect: () => void stopImpersonation(),
        }] : canStartImpersonation ? [{
          id: 'start-impersonation', label: '다른 사용자로 보기',
          icon: <SwitchAccountOutlined fontSize="small" />,
          dividerBefore: true,
          onSelect: () => { if (!confirmNavigation()) return; setFeatureViewAsTarget(undefined); setImpersonationDialogOpen(true); },
        }] : []), {
          id: 'logout', label: '로그아웃', destructive: true,
          icon: <LogoutOutlined fontSize="small" />,
          dividerBefore: true,
          onSelect: () => {
            if (!confirmNavigation()) return;
            if (fixtureMode) {
              requireLogin();
              return;
            }
            void client.logout().then(requireLogin, (reason: unknown) => {
              setAuthError(reason instanceof Error ? reason.message : '로그아웃하지 못했습니다.');
              setAuthState('error');
            });
          },
        }],
      }}
      onNavigate={navigate}
      tasks={{
        label: '내 할 일 열기',
        count: workflowTaskCount.error ? undefined : workflowTaskCount.data,
        onSelect: () => navigate('my-tasks'),
      }}
      search={<HeaderSearch
        key={JSON.stringify([
          session.principal.tenant, session.principal.personId, session.principal.workerId,
          session.impersonation?.id, session.impersonation?.readOnly, navigationMode,
          [...session.principal.scopes].sort(), [...session.principal.roles].sort(),
          [...session.principal.tenantScopes ?? []].sort(),
          session.principal.availableFeatures === undefined ? null : [...session.principal.availableFeatures].sort(),
          session.principal.managementApps === undefined ? null : [...session.principal.managementApps].sort(),
        ])}
        capability={{
          scopes: session.principal.scopes, fixtureMode: fixtureMode && !session.impersonation,
          tenantScopes: session.principal.tenantScopes,
          readOnly: Boolean(session.impersonation?.readOnly),
          availableFeatures: session.principal.availableFeatures, managementApps: session.principal.managementApps,
        }}
        visiblePages={navigation.visiblePages} mode={navigationMode}
        directoryPort={employeeDirectoryPort} fixtureMode={fixtureMode}
        enabled={!(immersiveWorkspace && pageReleased) && !impersonationDialogOpen}
        onNavigate={navigate}
      />}
      immersive={immersiveWorkspace && pageReleased}
    >
      <PageHeaderIconProvider icon={heydayPageIcons[currentPage]}>
        {!pageReleased ? <ResultState title="현재 사용할 수 없는 기능입니다" description={navigationMode === 'admin' ? '이 앱을 관리할 권한이 없습니다. 관리자에게 권한을 확인해 주세요.' : '이 계정에는 아직 공개되지 않은 앱입니다. 진행 중인 요청은 내 요청과 할 일에서 확인할 수 있습니다.'} action={<Button variant="fill" onClick={() => navigate('my-home')}>홈으로 이동</Button>} /> : <HeydayRouteContent
          featureReleasePort={featureReleasePort}
          onFeaturePolicySaved={async () => {
            // Complete an older focus refresh before fetching the just-saved policy.
            if (refreshSessionInFlight.current) await refreshSessionInFlight.current;
            await refreshSession();
          }}
          onFeatureViewAs={canStartImpersonation ? (target) => { if (!confirmNavigation()) return; setFeatureViewAsTarget(target); setImpersonationDialogOpen(true); } : undefined}
          currentPage={currentPage}
          absencePort={absencePort}
          absenceSettingsPort={absenceSettingsPort}
          bulkOperationsPort={bulkOperationsPort}
          bulkReferencePort={bulkReferencePort}
          certificatesPort={certificatesPort}
          approvalSettingsPort={approvalSettingsPort}
          workflowPort={workflowPort}
          workflowOperationsPort={workflowOperationsPort}
          workflowSelfDelegationPort={workflowSelfDelegationPort}
          headcountPort={headcountPort}
          integrationSettingsPort={integrationSettingsPort}
          tenantSettingsPort={tenantSettingsPort}
          employeeDirectoryPort={employeeDirectoryPort}
          documentsPort={documentsPort}
          employeeHomePort={employeeHomePort}
          inboxPort={inboxPort}
          operationsHomePort={operationsHomePort}
          administratorPort={administratorPort}
          rewardsApi={rewardsApi}
          contractApi={contractApi}
          staffingOperationsPort={staffingOperationsPort}
          organizationPort={organizationPort}
          peopleOperationsPort={peopleOperationsPort}
          lifecyclePort={lifecyclePort}
          peopleProfilePort={peopleProfilePort}
          recruitingPort={recruitingPort}
          session={session}
          currentPersonId={accountWorker.data?.person?.id ?? session.principal.personId}
          fixtureMode={fixtureMode}
          visiblePages={navigation.visiblePages}
          workflowSummary={{
            taskCount: workflowTaskCount.data,
            inProgressRequestCount: workflowRequestSummary.data?.inProgress,
            completedRequestCount: workflowRequestSummary.data?.completed,
            loading: workflowTaskCount.loading || workflowRequestSummary.loading,
            error: workflowTaskCount.error?.message || workflowRequestSummary.error?.message,
            onRetry: () => { workflowTaskCount.reload(); workflowRequestSummary.reload(); },
          }}
          onNavigate={navigate}
          onWorkflowTasksChanged={workflowTaskCount.reload}
          onWorkflowRequestsChanged={workflowRequestSummary.reload}
          onProfilePhotoChanged={accountWorker.reload}
          onOrganizationChanged={setOrganizationBrandingOverride}
        />}
      </PageHeaderIconProvider>
    </AppShell>
    <ImpersonationDialog
      open={impersonationDialogOpen}
      port={impersonationPort}
      initialIdentityId={featureViewAsTarget?.identityId}
      initialSearch={featureViewAsTarget?.displayName}
      onClose={() => setImpersonationDialogOpen(false)}
      onStarted={() => reloadAt('my-home')}
    />
    <FeedbackSnackbar
      open={Boolean(sessionActionError)}
      autoHideDuration={8000}
      message={sessionActionError}
      tone="error"
      onClose={() => setSessionActionError(undefined)}
    />
  </BrandThemeProvider>);
}
